import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpenIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getMyReadRequirements, HSE_READ_CHANGED_EVENT } from "../../../../api/hse/documentCompliance";
import { ReminderPortal } from "../../reminders/ReminderStack";
import { formatComplianceDate } from "./ComplianceBits";

export const HSE_READ_SNOOZE_MS = 60 * 60 * 1000;
const REFRESH_MS = 5 * 60 * 1000;
export const HANDBOOK_REQUIRED_PATH = "/dashboard/quality/employee-handbook?tab=documents&filter=required";
const storageKey = (contextKey) => `promang.hseReadReminderSnooze:${contextKey}`;

/** Pages where the reader is already working through the documents: no floating reminder there. */
export const hseReadReminderHiddenPath = (pathname) =>
  /^\/dashboard\/quality\/employee-handbook\/?$/.test(pathname) ||
  /^\/dashboard\/hse-ks\/documents(?:\/|$)/.test(pathname) ||
  /^\/dashboard\/(training(?:\/|$)|guide(?:\/|$))/.test(pathname);

/** New, revised or newly overdue requirements change the signature, so a snooze ends at once. */
export const hseReadSignature = (items = []) =>
  items.map((item) => `${item.requirement_id}:${item.due_at || ""}:${item.overdue ? 1 : 0}`).sort().join(",");

export function readHseReadSnooze(contextKey, storage = globalThis.localStorage) {
  try {
    const parsed = JSON.parse(storage?.getItem(storageKey(contextKey)) || "null");
    if (!parsed || typeof parsed.until !== "number") return null;
    return { until: parsed.until, signature: String(parsed.signature ?? "") };
  } catch {
    return null;
  }
}

export function writeHseReadSnooze(contextKey, signature, now = Date.now(), storage = globalThis.localStorage) {
  const value = { until: now + HSE_READ_SNOOZE_MS, signature };
  try {
    storage?.setItem(storageKey(contextKey), JSON.stringify(value));
  } catch {
    // Blocked storage: the snooze still applies for this page session via state.
  }
  return value;
}

export const isHseReadSnoozed = (snooze, signature, now = Date.now()) =>
  Boolean(snooze) && snooze.signature === signature && now < snooze.until;

/**
 * Floating «Les og forstå» reminder for required HMS documents, next to the training reminder
 * (PROMANG_COMPONENTS.md §6 «Flytende påminnelser», §8.5). Pending reading can be snoozed for an
 * hour; once a deadline has passed the reminder stays until the reader confirms or postpones.
 */
export function HseReadReminder() {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const userId = useSelector((state) => state.session.currentUser?.id);
  const authReady = useSelector((state) => Boolean(state.session.accessToken) && !state.session.loading);
  if (!companyId || !userId || !authReady) return null;
  return <HseReadReminderSession key={`${companyId}:${userId}`} contextKey={`${companyId}:${userId}`} />;
}

function HseReadReminderSession({ contextKey }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [snooze, setSnooze] = useState(() => readHseReadSnooze(contextKey));
  const [, setTick] = useState(0);
  const request = useRef(0);

  const refresh = useCallback(async () => {
    const current = ++request.current;
    try {
      const data = await getMyReadRequirements();
      if (current === request.current) setStatus(data);
    } catch {
      // A failed check is not evidence of a requirement: keep quiet until the next refresh.
      if (current === request.current) setStatus(null);
    }
  }, []);

  useEffect(() => {
    refresh();
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener(HSE_READ_CHANGED_EVENT, refresh);
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(visible, REFRESH_MS);
    return () => {
      request.current += 1;
      clearInterval(timer);
      window.removeEventListener(HSE_READ_CHANGED_EVENT, refresh);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [refresh]);

  const items = status?.enabled && status.count > 0 ? status.items || [] : [];
  const overdue = (status?.overdue_count || 0) > 0;
  const signature = hseReadSignature(items);
  const snoozed = !overdue && isHseReadSnoozed(snooze, signature);

  useEffect(() => {
    if (!snoozed) return undefined;
    const timer = setTimeout(() => setTick((tick) => tick + 1), Math.max(0, snooze.until - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [snoozed, snooze]);

  if (items.length === 0 || snoozed || hseReadReminderHiddenPath(location.pathname)) return null;

  const count = status.count;
  const first = items[0];
  const title = overdue
    ? t("hseCompliance.reminder.overdue_title", { count: status.overdue_count })
    : t("hseCompliance.reminder.title", { count });
  // Items come soonest deadline first: when something is overdue, say when it was due, not «next».
  const detail = overdue && first?.due_at
    ? t("hseCompliance.reminder.was_due", { date: formatComplianceDate(first.due_at, i18n.language) })
    : status.next_due_at
      ? t("hseCompliance.reminder.next_due", { date: formatComplianceDate(status.next_due_at, i18n.language) })
      : null;

  return (
    <ReminderPortal>
      <aside
        aria-label={t("hseCompliance.reminder.label")}
        className={`pm-alert ${overdue ? "pm-alert--bad" : "pm-alert--warn"} pointer-events-auto w-full max-w-2xl items-center shadow-lg`}
      >
        <span className="pm-alert__icon" aria-hidden="true">
          <BookOpenIcon className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="pm-alert__title">{title}</p>
          <p className="pm-alert__desc truncate">
            {[detail, items.map((item) => item.title).join(" · ")].filter(Boolean).join(" — ")}
          </p>
        </div>
        <Link to={count === 1 ? first.url : HANDBOOK_REQUIRED_PATH} className="pm-btn pm-btn--secondary pm-btn--sm shrink-0">
          {count === 1 ? t("hseCompliance.reminder.open_one") : t("hseCompliance.reminder.open_all")}
        </Link>
        {!overdue ? (
          <button
            type="button"
            className="-mr-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[color:var(--pls-muted)] hover:bg-[color:var(--pls-surface2)] hover:text-[color:var(--pls-fg)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1463C7]"
            onClick={() => setSnooze(writeHseReadSnooze(contextKey, signature))}
            aria-label={t("hseCompliance.reminder.snooze")}
            title={t("hseCompliance.reminder.snooze")}
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </aside>
    </ReminderPortal>
  );
}

export default HseReadReminder;
